/* Progressive enhancement only. Navigation, options and cart work without JS. */
(() => {
  const controllers = new WeakMap();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Keep anchor targets and keyboard focus below the full, non-shrinking header.
  const header = document.querySelector('.site-header');
  if (header) {
    const updateHeaderHeight = () =>
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    new ResizeObserver(updateHeaderHeight).observe(header);
    updateHeaderHeight();
  }

  function initCarousels(root = document) {
    root.querySelectorAll('[data-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-track]');
      const buttons = carousel.querySelectorAll('[data-scroll]');
      if (!track || track.dataset.ready) return;
      track.dataset.ready = 'true';
      const update = () => {
        const limit = track.scrollWidth - track.clientWidth;
        buttons.forEach((button) => {
          button.hidden = limit < 2;
          button.disabled =
            Number(button.dataset.scroll) < 0
              ? track.scrollLeft < 2
              : track.scrollLeft >= limit - 2;
        });
      };
      buttons.forEach((button) =>
        button.addEventListener('click', () => {
          const card = track.firstElementChild;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          track.scrollBy({
            left:
              Number(button.dataset.scroll) *
              ((card?.getBoundingClientRect().width || track.clientWidth) + gap),
            behavior: reducedMotion() ? 'instant' : 'smooth',
          });
        }),
      );
      track.addEventListener('scroll', update, { passive: true });
      const observer = new ResizeObserver(update);
      observer.observe(track);
      carousel.addEventListener('roble:dispose', () => observer.disconnect(), { once: true });
      update();
    });
  }

  // One native dialog per product section. All triggers are delegated so variant
  // section replacement and Shopify editor reloads keep the gallery functional.
  const galleries = new WeakMap();
  function showGalleryImage(dialog, index) {
    const state = galleries.get(dialog);
    state.index = (index + state.items.length) % state.items.length;
    const item = state.items[state.index];
    const image = dialog.querySelector('[data-gallery-image]');
    image.src = item.href;
    image.alt = item.dataset.mediaAlt;
    dialog.querySelector('[data-gallery-caption]').textContent = image.alt;
    dialog.querySelector('[data-gallery-counter]').textContent =
      `${state.index + 1} / ${state.items.length}`;
    const canvas = dialog.querySelector('[data-gallery-canvas]');
    canvas.classList.remove('is-zoomed');
    canvas.scrollTo(0, 0);
    dialog.querySelector('[data-gallery-zoom]').setAttribute('aria-pressed', 'false');
    dialog.querySelectorAll('[data-gallery-step]').forEach((button) => {
      button.disabled = state.items.length < 2;
    });
  }
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-gallery-open]');
    if (
      trigger &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      const dialog = trigger
        .closest('[data-product-section]')
        ?.querySelector('[data-product-lightbox]');
      if (!dialog?.showModal) return; // The full-size image URL remains a no-JS fallback.
      event.preventDefault();
      const items = [
        ...trigger.closest('[data-product-gallery]').querySelectorAll('[data-gallery-open]'),
      ];
      galleries.set(dialog, { items, index: 0, trigger });
      showGalleryImage(dialog, items.indexOf(trigger));
      dialog.showModal();
      document.body.classList.add('gallery-open');
      return;
    }
    const thumb = event.target.closest('[data-media-thumbnail]');
    if (
      thumb &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      const target = document.getElementById(thumb.hash.slice(1));
      if (target) {
        event.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
    const dialog = event.target.closest('[data-product-lightbox]');
    if (!dialog?.open) return;
    if (event.target.closest('[data-gallery-close]')) dialog.close();
    const step = event.target.closest('[data-gallery-step]');
    if (step)
      showGalleryImage(dialog, galleries.get(dialog).index + Number(step.dataset.galleryStep));
    if (
      event.target.closest('[data-gallery-zoom]') ||
      event.target.closest('.is-zoomed [data-gallery-image]')
    ) {
      const zoomed = dialog.querySelector('[data-gallery-canvas]').classList.toggle('is-zoomed');
      dialog.querySelector('[data-gallery-zoom]').setAttribute('aria-pressed', String(zoomed));
    }
  });
  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches('[data-product-lightbox]')) return;
      document.body.classList.remove('gallery-open');
      galleries.get(event.target)?.trigger?.focus({ preventScroll: true });
    },
    true,
  );
  document.addEventListener('keydown', (event) => {
    const dialog = event.target.closest('[data-product-lightbox]');
    if (!dialog?.open || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    if (dialog.querySelector('.is-zoomed')) return; // Arrow keys can pan the enlarged photo.
    event.preventDefault();
    showGalleryImage(dialog, galleries.get(dialog).index + (event.key === 'ArrowLeft' ? -1 : 1));
  });

  async function changeOption(link, section) {
    controllers.get(section)?.abort();
    const controller = new AbortController();
    controllers.set(section, controller);
    const destination = new URL(link.href);
    const request = new URL(destination);
    request.searchParams.set('section_id', section.dataset.productSection);
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('[name="add"], .shopify-payment-button button').forEach((button) => {
      button.disabled = true;
    });
    try {
      const response = await fetch(request, {
        signal: controller.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Section request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-product-state]');
      if (!next) throw new Error('Missing product section');
      const quantity = section.querySelector('[name="quantity"]')?.value;
      section.querySelector('[data-product-state]').replaceWith(next);
      const input = next.querySelector('[name="quantity"]');
      if (input && quantity) {
        const min = Number(input.min) || 1;
        const step = Number(input.step) || 1;
        const max = Number(input.max) || Infinity;
        const snapped = min + Math.floor((Number(quantity) - min) / step) * step;
        input.value = Math.max(min, Math.min(max, snapped));
      }
      history.replaceState({}, '', destination);
      document.getElementById(link.id)?.focus({ preventScroll: true });
      window.Shopify?.PaymentButton?.init?.();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(destination);
    } finally {
      if (controllers.get(section) === controller) section.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-option-link]');
    if (
      link &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      const section = link.closest('[data-product-section]');
      // Cross-product / combined-listing links use ordinary navigation.
      if (section && new URL(link.href).pathname === section.dataset.productUrl) {
        event.preventDefault();
        changeOption(link, section);
      }
    }
    document.querySelectorAll('.header details[open]').forEach((detail) => {
      if (!detail.contains(event.target) || event.target.closest('a')) detail.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.header details[open]').forEach((detail) => {
      detail.open = false;
      detail.querySelector('summary')?.focus();
    });
  });
  document.addEventListener('shopify:section:load', (event) => initCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target
      .querySelectorAll('[data-carousel]')
      .forEach((el) => el.dispatchEvent(new Event('roble:dispose')));
    if (event.target.querySelector('[data-product-lightbox][open]'))
      document.body.classList.remove('gallery-open');
    event.target
      .querySelectorAll('[data-product-section]')
      .forEach((el) => controllers.get(el)?.abort());
  });
  initCarousels();
})();
